import React, { useMemo, useEffect, useState } from "react";
import { Box, Text, VStack } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { AdCreativeFormData } from "../../forms/adcreative";
import { InstagramAccount } from "../../types";
import InstagramPostPreview from "./InstagramPostPreview";
import InstagramStoryPreview from "./InstagramStoryPreview";
import { analyzePlacementRequirements } from "src/utils/aspectRatio";
import { mapInstagramPlacementToAspectKey } from "src/utils/aspectRatio/placementMap";

interface InstagramPreviewProps {
  instagramAccounts?: InstagramAccount[];
}

const formatsToPreview = {
  square: [InstagramPostPreview],
  portrait: [InstagramStoryPreview],
};
const formatToLabel = {
  square: "Feed",
  portrait: "Story",
};

const InstagramPreview: React.FC<InstagramPreviewProps> = ({
  instagramAccounts = [],
}) => {
  const { watch } = useFormContext<AdCreativeFormData>();

  // Get form data
  const message = watch("message") || "";
  const callToAction = watch("call_to_action");
  const instagramActor = watch("instagram_actor_id");
  const page = watch("page_id");
  const images = watch("images");
  const videos = watch("videos");
  const mediaType = watch("media_type");
  const instagramPlacements = watch("instagram_placements") || [];
  const placements = useMemo(
    () => instagramPlacements.map(mapInstagramPlacementToAspectKey),
    [instagramPlacements.join(",")]
  );
  const [formats, setFormats] = useState<("square" | "portrait")[]>([
    "square",
    "portrait",
  ]);

  useEffect(() => {
    const analysis = analyzePlacementRequirements(placements);

    if (!placements.length) {
      setFormats(["square", "portrait"]);
      return;
    }

    setFormats([
      ...(analysis.needsSquare ? ["square"] : []),
      ...(analysis.needsPortrait ? ["portrait"] : []),
    ] as ("square" | "portrait")[]);
  }, [placements]);

  // Get media for display - handle carousel vs single media types
  const squareImages = images?.square || [];
  const portraitImages = images?.portrait || [];
  const squareVideos = videos?.square || [];
  const portraitVideos = videos?.portrait || [];

  // Collect media URLs for each format
  const squareMediaUrls = [
    ...squareImages.map((img) => img.picture_url),
    ...squareVideos.map((vid) => vid.source),
  ].filter(Boolean);

  const portraitMediaUrls = [
    ...portraitImages.map((img) => img.picture_url),
    ...portraitVideos.map((vid) => vid.source),
  ].filter(Boolean);

  // Get username from Instagram account or page
  const username = instagramActor?.label || page?.label || "Instagram Account";
  const profilePicture = instagramAccounts?.find(
    (account) => account.id === instagramActor?.value
  )?.profile_picture_url;
  const ctaText = callToAction?.label || "Order Now";

  const mediaUrls = {
    portrait: portraitMediaUrls,
    square: mediaType === "carousel" ? portraitMediaUrls : squareMediaUrls,
  };

  return (
    <VStack
      bg="gray.400"
      p={4}
      borderRadius="lg"
      display="flex"
      w="full"
      justifyContent="center"
      alignItems="center"
      h="full"
      gap={[4, 8]}
    >
      {formats.map((format) =>
        formatsToPreview[format].map((Preview) => (
          <VStack key={format} w="full">
            <Text fontWeight={"bold"} color="gray.700">
              {formatToLabel[format]}
            </Text>
            <Preview
              username={username}
              message={message}
              ctaText={ctaText}
              mediaUrls={mediaUrls[format]}
              mediaType={mediaType as "single" | "carousel"}
              profilePictureUrl={profilePicture}
            />
          </VStack>
        ))
      )}
    </VStack>
  );
};
export default InstagramPreview;
